import {
  DECLARED_TAX_CELL_KEYS,
  type DeclaredTaxCell, type DeclaredTaxCellKey, type DeclaredTaxProfile, type DeclaredTaxProfileInput, type DeclaredTaxTreatment,
} from './types';

/**
 * The declared tax profile of a custom benefit or deduction (contract 4.1.1.1 and 4.2.1): the treatment (Pre-tax or Post-tax) and one choice per tax.
 * It is recorded as "needs review" and read by nothing in payroll or the tax engine until a profile is approved. The Admin decides every tax of a Pre-tax item:
 * no choice is ever preselected, and "Pre-tax" is never taken to mean exempt from every tax. A Post-tax item may leave a tax undecided.
 */

export type CellChoice = DeclaredTaxCell | '';

export interface DeclaredTaxFormState {
  treatment: DeclaredTaxTreatment | '';
  cells: Record<DeclaredTaxCellKey, CellChoice>;
}

export const emptyDeclaredTaxForm = (): DeclaredTaxFormState => ({
  treatment: '',
  cells: { federalIncomeTax: '', socialSecurity: '', medicare: '', stateIncomeTax: '', localIncomeTax: '' },
});

export interface DeclaredTaxIssues {
  treatment?: string;
  /** Pre-tax only: one message naming the taxes still to decide. */
  cells?: string;
  /** The taxes still to decide, in display order. */
  missing: DeclaredTaxCellKey[];
}

/** The words the server uses when it names the taxes still to decide, so the form and the server say the same thing. */
const SENTENCE_LABELS: Record<DeclaredTaxCellKey, string> = {
  federalIncomeTax: 'federal income tax', socialSecurity: 'Social Security', medicare: 'Medicare', stateIncomeTax: 'state income tax', localIncomeTax: 'local income tax',
};

const names = (keys: DeclaredTaxCellKey[]): string => {
  const labels = keys.map((k) => SENTENCE_LABELS[k]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** What is still missing on the form. `required` is true for a custom item created in Employee Profile Setup (the treatment is required there). */
export function declaredTaxIssues(state: DeclaredTaxFormState, required: boolean): DeclaredTaxIssues {
  const missing = DECLARED_TAX_CELL_KEYS.filter((k) => state.cells[k] === '');
  const issues: DeclaredTaxIssues = { missing: state.treatment === 'PreTax' ? [...missing] : [] };
  if (state.treatment === '') {
    if (required) issues.treatment = 'Choose Pre-tax or Post-tax.';
    return issues;
  }
  if (state.treatment === 'PreTax' && missing.length > 0) issues.cells = `Choose Exempt or Taxable for every tax. Still needed: ${names(missing)}.`;
  return issues;
}

export const hasDeclaredTaxIssues = (issues: DeclaredTaxIssues): boolean => issues.treatment !== undefined || issues.cells !== undefined;

/** The wire object for a complete form, or null when no treatment was chosen. */
export function declaredTaxFormToInput(state: DeclaredTaxFormState): DeclaredTaxProfileInput | null {
  if (state.treatment === '') return null;
  const cell = (k: DeclaredTaxCellKey): DeclaredTaxCell | null => (state.cells[k] === '' ? null : state.cells[k]);
  return {
    treatment: state.treatment,
    federalIncomeTax: cell('federalIncomeTax'),
    socialSecurity: cell('socialSecurity'),
    medicare: cell('medicare'),
    stateIncomeTax: cell('stateIncomeTax'),
    localIncomeTax: cell('localIncomeTax'),
  };
}

/** A custom item made from Employee Profile Setup also needs a description and an effective date (the spec's required set); the People pages keep their own rules. */
export function inlineCreateIssues(form: { description: string; effectiveStartDate: string }): { field: string; message: string }[] {
  const issues: { field: string; message: string }[] = [];
  if (form.description.trim() === '') issues.push({ field: 'description', message: 'Enter a short description.' });
  if (form.effectiveStartDate.trim() === '') issues.push({ field: 'effectiveStartDate', message: 'Choose the date this takes effect.' });
  return issues;
}

export type DeclaredTaxServerRefusal = { code: 'INVALID_VALUE' | 'DECLARED_TAX_PROFILE_INCOMPLETE'; message: string };

/** The server's own rules for a declared profile, for the mock gateways: null when it is acceptable. */
export function declaredTaxRefusal(profile: DeclaredTaxProfileInput | null | undefined): DeclaredTaxServerRefusal | null {
  if (!profile) return null;
  if (profile.treatment !== 'PreTax' && profile.treatment !== 'PostTax') {
    return { code: 'INVALID_VALUE', message: 'declaredTaxProfile.treatment is required and must be PreTax or PostTax.' };
  }
  const missing: DeclaredTaxCellKey[] = [];
  for (const key of DECLARED_TAX_CELL_KEYS) {
    const value = profile[key] as unknown;
    if (value === null || value === undefined) missing.push(key);
    else if (value !== 'Exempt' && value !== 'Taxable') return { code: 'INVALID_VALUE', message: `declaredTaxProfile.${key} must be Exempt or Taxable.` };
  }
  if (profile.treatment === 'PreTax' && missing.length > 0) {
    return { code: 'DECLARED_TAX_PROFILE_INCOMPLETE', message: `A pre-tax item needs a choice of Exempt or Taxable for every tax. Still needed: ${names(missing)}.` };
  }
  return null;
}

/** What the server stores and returns for a profile it accepted: the declared values, marked "needs review". */
export function declaredFromInput(profile: DeclaredTaxProfileInput | null | undefined, now = new Date().toISOString()): DeclaredTaxProfile | null {
  if (!profile) return null;
  return {
    treatment: profile.treatment,
    federalIncomeTax: profile.federalIncomeTax, socialSecurity: profile.socialSecurity, medicare: profile.medicare,
    stateIncomeTax: profile.stateIncomeTax, localIncomeTax: profile.localIncomeTax,
    status: 'NeedsReview', declaredAtUtc: now,
  };
}
